import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * useVoiceRecognition - Custom React hook for Web Speech API integration
 *
 * Features:
 * - Real-time speech-to-text transcription
 * - Automatic restart on error
 * - Browser compatibility detection
 * - Continuous listening mode
 * - Interim and final results
 *
 * Supported Browsers: Chrome, Edge, Safari
 */

interface UseVoiceRecognitionReturn {
  transcript: string;
  interimTranscript: string;
  isListening: boolean;
  isSupported: boolean;
  error: string | null;
  startListening: () => void;
  stopListening: () => void;
  resetTranscript: () => void;
}

// Web Speech API type definitions
interface SpeechRecognitionResult {
  isFinal: boolean;
  [index: number]: SpeechRecognitionAlternative;
}

interface SpeechRecognitionAlternative {
  transcript: string;
  confidence: number;
}

interface SpeechRecognitionResultList {
  length: number;
  item(index: number): SpeechRecognitionResult;
  [index: number]: SpeechRecognitionResult;
}

interface SpeechRecognitionEvent extends Event {
  resultIndex: number;
  results: SpeechRecognitionResultList;
}

interface SpeechRecognitionErrorEvent extends Event {
  error: string;
  message: string;
}

interface SpeechRecognition extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  maxAlternatives: number;

  onresult: ((event: SpeechRecognitionEvent) => void) | null;
  onstart: (() => void) | null;
  onend: (() => void) | null;
  onerror: ((event: SpeechRecognitionErrorEvent) => void) | null;
  onnomatch: (() => void) | null;
  onaudiostart: (() => void) | null;
  onaudioend: (() => void) | null;
  onsoundstart: (() => void) | null;
  onsoundend: (() => void) | null;
  onspeechstart: (() => void) | null;
  onspeechend: (() => void) | null;

  start(): void;
  stop(): void;
  abort(): void;
}

interface SpeechRecognitionConstructor {
  new(): SpeechRecognition;
}

// Extend Window interface for Web Speech API types
declare global {
  interface Window {
    SpeechRecognition: SpeechRecognitionConstructor;
    webkitSpeechRecognition: SpeechRecognitionConstructor;
  }
}

export const useVoiceRecognition = (): UseVoiceRecognitionReturn => {
  const [transcript, setTranscript] = useState('');
  const [interimTranscript, setInterimTranscript] = useState('');
  const [isListening, setIsListening] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const recognitionRef = useRef<SpeechRecognition | null>(null);
  const isInitializedRef = useRef(false);

  // Check browser support
  const isSupported =
    typeof window !== 'undefined' &&
    ('SpeechRecognition' in window || 'webkitSpeechRecognition' in window);

  // Initialize Speech Recognition
  useEffect(() => {
    if (!isSupported || isInitializedRef.current) return;

    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;

    if (!SpeechRecognition) return;

    const recognition = new SpeechRecognition();

    // Configuration
    recognition.continuous = true; // Keep listening until explicitly stopped
    recognition.interimResults = true; // Get interim results while speaking
    recognition.lang = 'en-US';
    recognition.maxAlternatives = 1;

    // Event: Result received
    recognition.onresult = (event: Event) => {
      const speechEvent = event as SpeechRecognitionEvent;
      let interimText = '';
      let finalText = '';

      for (let i = speechEvent.resultIndex; i < speechEvent.results.length; i++) {
        const result = speechEvent.results[i];
        const transcriptText = result[0].transcript;

        if (result.isFinal) {
          finalText += transcriptText + ' ';
        } else {
          interimText += transcriptText;
        }
      }

      setInterimTranscript(interimText);
      if (finalText) {
        setTranscript((prev) => (prev + finalText).trim());
      }
    };

    // Event: Recognition started
    recognition.onstart = () => {
      setIsListening(true);
      setError(null);
      console.log('🎤 Voice recognition started');
    };

    // Event: Recognition ended
    recognition.onend = () => {
      setIsListening(false);
      console.log('🎤 Voice recognition ended');
    };

    // Event: Error occurred
    recognition.onerror = (event: Event) => {
      const errorEvent = event as SpeechRecognitionErrorEvent;
      console.error('🎤 Voice recognition error:', errorEvent.error);

      setError(errorEvent.error);
      setIsListening(false);

      // Handle specific errors
      switch (errorEvent.error) {
        case 'no-speech':
          console.log('No speech detected. Please try again.');
          break;
        case 'audio-capture':
          console.error('Microphone not accessible. Please check permissions.');
          break;
        case 'not-allowed':
          console.error('Microphone permission denied.');
          break;
        case 'network':
          console.error('Network error occurred.');
          break;
        default:
          console.error('Recognition error:', errorEvent.error);
      }
    };

    // Event: No speech detected
    recognition.onnomatch = () => {
      console.log('🎤 No speech was recognized');
    };

    // Event: Audio starts
    recognition.onaudiostart = () => {
      console.log('🎤 Audio capturing started');
    };

    // Event: Audio ends
    recognition.onaudioend = () => {
      console.log('🎤 Audio capturing ended');
    };

    // Event: Sound starts
    recognition.onsoundstart = () => {
      console.log('🎤 Sound detected');
    };

    // Event: Sound ends
    recognition.onsoundend = () => {
      console.log('🎤 Sound ended');
    };

    // Event: Speech starts
    recognition.onspeechstart = () => {
      console.log('🎤 Speech detected');
    };

    // Event: Speech ends
    recognition.onspeechend = () => {
      console.log('🎤 Speech ended');
    };

    recognitionRef.current = recognition;
    isInitializedRef.current = true;

    return () => {
      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop();
        } catch (e) {
          // Recognition might not be running
        }
      }
    };
  }, [isSupported]);

  // Start listening
  const startListening = useCallback(() => {
    if (!recognitionRef.current || isListening) return;

    try {
      setError(null);
      recognitionRef.current.start();
    } catch (error) {
      console.error('Failed to start recognition:', error);
      setError('Failed to start recognition');
    }
  }, [isListening]);

  // Stop listening
  const stopListening = useCallback(() => {
    if (!recognitionRef.current || !isListening) return;

    try {
      recognitionRef.current.stop();
    } catch (error) {
      console.error('Failed to stop recognition:', error);
    }
  }, [isListening]);

  // Reset transcript
  const resetTranscript = useCallback(() => {
    setTranscript('');
    setInterimTranscript('');
    setError(null);
  }, []);

  return {
    transcript,
    interimTranscript,
    isListening,
    isSupported,
    error,
    startListening,
    stopListening,
    resetTranscript,
  };
};
